import { QUALITY_STYLES, QUALITY_DOT, STAGE_STYLES } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { stageColor } from "@/lib/stageColors";

export function QualityChip({ value, className = "" }) {
  if (!value) return <span className="text-slate-300 text-xs">—</span>;
  return (
    <span
      data-testid={`quality-chip-${value}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${QUALITY_STYLES[value] || "bg-slate-50 text-slate-600 border-slate-200"} ${className}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${QUALITY_DOT[value] || "bg-slate-400"}`} />
      {value}
    </span>
  );
}

export function StageChip({ value, className = "" }) {
  const { options } = useAuth();
  if (!value) return <span className="text-slate-300 text-xs">—</span>;
  const meta = options?.stage_meta?.[value];
  const label = meta?.name || value;
  const cls = meta?.color ? stageColor(meta.color).soft : (STAGE_STYLES[value] || "bg-slate-50 text-slate-600 border-slate-200");
  return (
    <span
      data-testid={`stage-chip-${value}`}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${cls} ${className}`}
    >
      {label}
    </span>
  );
}

export function StatusDot({ status }) {
  const map = { Active: "bg-emerald-500", Paused: "bg-amber-500", Inactive: "bg-slate-300" };
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-ink/70">
      <span className={`h-2 w-2 rounded-full ${map[status] || "bg-slate-300"}`} />
      {status}
    </span>
  );
}

const SLA_META = {
  at_risk: { label: "At-risk", cls: "bg-red-50 text-red-600 border-red-200" },
  breached: { label: "SLA missed", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  on_time: { label: "On-time", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pending: { label: "On track", cls: "bg-slate-50 text-slate-500 border-slate-200" },
};

// SLA badge. By default (compact) it only renders the attention states (at-risk / missed);
// pass `full` to also show on-time / on-track (used in the Lead drawer).
export function SlaChip({ sla, full = false, className = "" }) {
  if (!sla || !sla.status) return null;
  if (!full && !["at_risk", "breached"].includes(sla.status)) return null;
  const m = SLA_META[sla.status];
  if (!m) return null;
  return (
    <span
      data-testid={`sla-chip-${sla.status}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${m.cls} ${className}`}
      title={`${Math.round(sla.minutes || 0)} business min since lead generated`}
    >
      {m.label}
    </span>
  );
}

const ACTIVITY_TYPE_STYLES = {
  Call: "bg-blue-50 text-blue-700 border-blue-200",
  "Follow-up": "bg-sky-50 text-sky-700 border-sky-200",
  Meeting: "bg-cyan-50 text-cyan-700 border-cyan-200",
  "Send Proposal": "bg-violet-50 text-violet-700 border-violet-200",
  "Send Deck": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Send Pricing": "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  "WhatsApp Message": "bg-emerald-50 text-emerald-700 border-emerald-200",
  Email: "bg-teal-50 text-teal-700 border-teal-200",
  "Internal Review": "bg-slate-100 text-slate-600 border-slate-200",
  "Payment Discussion": "bg-amber-50 text-amber-700 border-amber-200",
  "Contract / Agreement": "bg-orange-50 text-orange-700 border-orange-200",
  "Onboarding Step": "bg-lime-50 text-lime-700 border-lime-200",
  Other: "bg-slate-50 text-slate-600 border-slate-200",
};

const PRIORITY_STYLES = {
  Low: "bg-slate-100 text-slate-600 border-slate-200",
  Medium: "bg-blue-50 text-blue-700 border-blue-200",
  High: "bg-amber-50 text-amber-700 border-amber-200",
  Urgent: "bg-red-50 text-red-600 border-red-200",
};

const STATUS_STYLES = {
  Pending: "bg-sky-50 text-sky-700 border-sky-200",
  Snoozed: "bg-amber-50 text-amber-700 border-amber-200",
  Completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

export function ActivityTypeChip({ value, className = "" }) {
  if (!value) return null;
  return (
    <span data-testid={`activity-type-${value}`} className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${ACTIVITY_TYPE_STYLES[value] || "bg-slate-50 text-slate-600 border-slate-200"} ${className}`}>
      {value}
    </span>
  );
}

export function PriorityChip({ value, className = "" }) {
  if (!value) return null;
  return (
    <span data-testid={`priority-chip-${value}`} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${PRIORITY_STYLES[value] || "bg-slate-100 text-slate-600 border-slate-200"} ${className}`}>
      {value}
    </span>
  );
}

export function ActivityStatusChip({ value, className = "" }) {
  if (!value) return null;
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[value] || "bg-slate-50 text-slate-600 border-slate-200"} ${className}`}>
      {value}
    </span>
  );
}
